import React from "react";
import { AlertCircle } from "lucide-react";
import { Input } from "components/ui/input";
import { Alert, AlertDescription } from "components/ui/alert";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "components/ui/dialog";
import { Label } from "components/ui/label";

interface LinkCnDialogProps {
  searchedLhc: string;
  linkModalOpen: boolean;
  setLinkModalOpen: (open: boolean) => void;
  cnInput: string;
  setCnInput: (val: string) => void;
  cnInputError: string;
  setCnInputError: (val: string) => void;
  linking: boolean;
  linkError: string | null;
  onSubmitLink: () => void;
}

export const LinkCnDialog: React.FC<LinkCnDialogProps> = ({
  searchedLhc,
  linkModalOpen,
  setLinkModalOpen,
  cnInput,
  setCnInput,
  cnInputError,
  setCnInputError,
  linking,
  linkError,
  onSubmitLink,
}) => {
  return (
    <Dialog open={linkModalOpen} onOpenChange={setLinkModalOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900">
            Link New CN Number
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs font-medium text-gray-700">LHC Number</Label>
            <Input
              value={searchedLhc}
              disabled
              className="mt-1 h-9 text-xs bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          <div>
            <Label htmlFor="cn-number-input" className="text-xs font-medium text-gray-700">
              Enter CN Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="cn-number-input"
              placeholder="CN Number"
              value={cnInput}
              onChange={(e) => {
                setCnInput(e.target.value);
                setCnInputError("");
              }}
              className="mt-1 h-9 text-xs focus-visible:ring-black"
            />
            {cnInputError && (
              <p className="text-xs text-red-500 mt-1 font-medium">{cnInputError}</p>
            )}
          </div>

          {linkError && (
            <Alert variant="destructive" className="mt-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs font-medium">{linkError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex items-center justify-end gap-2 sm:space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={linking}
            onClick={() => setLinkModalOpen(false)}
            className="h-8 text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={linking}
            onClick={onSubmitLink}
            className="h-8 text-xs bg-black text-white hover:bg-gray-800 flex items-center gap-1.5"
          >
            {linking ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                Submitting...
              </>
            ) : ("Submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
